import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Upload } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import isArray from 'lodash/isArray';
import size from 'lodash/size';
import isEmpty from 'lodash/isEmpty';
import DownloadSingleFile from 'components/downloadSingleFile';
import {
  setValidity,
  updateFormField,
  triggerLoading,
  clearExcelError,
  deleteDocument,
  saveTask,
  addContractDate,
  deleteContractDate,
} from 'store/forms/actions';
import { ReactComponent as WarningRed } from 'assets/icons/warning-red.svg';
import { UploadLabel, UploadTitle, File, ClickUpload } from './components';
import { isNil, pick } from 'lodash';
import { handleSave } from 'utils';
import Button from 'components/button';
import { WarningModal } from 'components/layout/components';

const { Dragger } = Upload;

const Draggable = ({
  item,
  reset,
  task,
  value,
  key = '',
  index,
  group,
  mode,
  disabled,
  withOpen,
  handleFile = () => {},
  ...rest
}) => {
  const {
    label,
    subLabel,
    linkText,
    multiple,
    single,
    singlePerRow,
    fileCode,
    required,
    fileAccepts,
    fileLimit = 20,
  } = item;
  const [percent, setPercent] = useState(0);
  const [errorFile, setError] = useState('');
  const [notAccepted, setNotAccepted] = useState(false);
  const [excel, setExcel] = useState(false);
  const params = useParams();
  const isExcelToParse = fileCode === 'ibaBusXls';
  const dispatch = useDispatch();
  const forms = useSelector(state => state.forms);
  const docs = useSelector(state => state.forms[group]);
  const excelError = useSelector(state => state.forms.excelError);
  const shouldDisplayExcelError =
    !isEmpty(excelError) && excelError.name && isExcelToParse;
  const [errorMessages, setErrorMessages] = useState();
  const [excelErrorModalVisible, setExcelErrorModalVisible] = useState(false);
  const [errorDescription, setErrorDescription] = useState('');
  
  const [newDocs, setNewDocs] = useState([]);
  let registerLabel = null;

  useEffect(() => {
    setError('');
    setErrorDescription('');
  }, [reset]);

  useEffect(() => {
    if (multiple || single || singlePerRow) {
      if (docs[fileCode]) {
        setNewDocs([...docs[fileCode]]);
      }
    }
  }, [docs, fileCode, multiple, single, singlePerRow]);

  const saveData = {
    state: { ...forms },
    dispatch,
    saveTask,
    taskId: params?.taskId,
    formId: forms?.formId,
    isSaved: forms?.carrier?.rejectReasons?.isSubmitValid ? true : false,
  };

  const isInvalidType = (mode, type, fileCode) => {
    return mode === 'investmentExpenses'
      ? !(
          type.includes('sheet') ||
          type.includes('ms-excel')
        )
      : mode === 'declarations'
      ? !(
        type.includes('pdf') ||
        type.includes('png') ||
        type.includes('jpeg')
      )
      : fileCode == 'businessPlanFile'
      ? !(
        (type.includes('document') && !type.includes('presentation'))
      )
      : fileCode == 'eligibilityFile'
      ? !(
        type.includes('sheet.macroenabled') ||
        type.includes('sheet.macroEnabled')
      )
      : !(
          type.includes('pdf') ||
          type.includes('sheet') ||
          type.includes('png') ||
          type.includes('jpeg') ||
          (type.includes('document') && !type.includes('presentation')) ||
          type.includes('ms-excel')
        );
  };

  if (fileCode === 'accNum' && required)
    registerLabel = (
      <span className="custom-label-docs">
        <span>Λογαριασμός τελευταίου 2μήνου</span>
        <span className="color-red"> *</span>
      </span>
    );

  const props = {
    name: 'file',
    multiple,
    withCredentials: true,
    accept: fileAccepts,
    action:
      '/' +
      process.env.REACT_APP_BACKEND_FLOWABLE_CONTEXT_PATH +
      `${
        mode === 'investmentExpenses'
          ? `/app/rest/${params.taskId}/upload`
          : `/app/rest/tasks/${params.taskId}/raw-contents`
      }`,
    openFileDialogOnClick:
      single ||
      singlePerRow ||
      multiple ||
      (!multiple && size(docs[fileCode]) < 1),
    beforeUpload: file => {
      const fileExceeds = file.size > fileLimit * 1024 * 1024;
      if (fileExceeds) {
        if (multiple) {
          return false;
        }
        if (single && docs[fileCode] && docs[fileCode][index]) {
          dispatch(
            updateFormField({
              group,
              key: fileCode,
              value:
                size(docs[fileCode]) === 1
                  ? ''
                  : docs[fileCode].map(doc => {
                      if (!isNil(doc) && doc.id === file.id) {
                        return '';
                      }
                      return doc;
                    }),
            })
          );
        }
        setError(file.name);
        setErrorDescription(`Το μέγεθος του αρχείου ξεπερνάει τα ${fileLimit} MB`)
        dispatch(triggerLoading(false));
        return false;
      } else if (isInvalidType(mode, file.type, fileCode)) {
        setNotAccepted(true);
        setErrorDescription('Ο τύπος του αρχείου δεν είναι αποδεκτός')
        setError(file.name);
        return false;
      } else {
        setError('');
        setErrorDescription('');
        dispatch(triggerLoading(false));
        setNotAccepted(false);
        if (mode === 'ktp-uploads') {
          handleFile(file);
          dispatch(triggerLoading(false));
          //for proper appearance of doc on ui:
          dispatch(
            updateFormField({
              group,
              key: fileCode,
              value: {
                ...pick(file, ['uid', 'name', 'size', 'type', 'lastModified']),
              },
            })
          );
          return false; //stop from executing action
        }
        dispatch(triggerLoading(false));
        return true;
      }
    },
    onChange: info => {
      dispatch(triggerLoading(true));
      const { status, size, type } = info.file;

      if (status === 'error') {
        const responseStatus = info?.file?.response?.status || info?.file?.error?.status;
    
        if (responseStatus == 400 && fileCode == "investmentExpenses") {
          setExcel(true)
          setErrorDescription('Σφάλμα κατα τη μεταφόρτωση του αρχείου');
          dispatch(triggerLoading(false));
          setExcelErrorModalVisible(true);
          setErrorMessages(info?.file?.response?.errors?.map(
            error => error.errorMessage
          ))
          setError(`Βεβαιωθείτε ότι έχετε συμπληρώσει όλα τα υποχρεωτικά πεδία στο αρχείο ${info.file.name}`);
        }
        else if (responseStatus == 500) {
          setErrorDescription('Παρουσιάστηκε σφάλμα κατά τη μεταφόρτωση του αρχείου. Παρακαλούμε δοκιμάστε ξανά.')
          setExcel(false);
          dispatch(triggerLoading(false));
          setError(`${info.file.name}`);

        }
        else {
          setErrorDescription('')
          setExcel(false);
        }
      }

      const fileExceeds = size > fileLimit * 1024 * 1024;
      if (fileExceeds) {
        // setError(info.file.name);
        dispatch(triggerLoading(false));
        return;
      }

      if (isInvalidType(mode, type, fileCode)) {
        dispatch(triggerLoading(false));
        return;
      }
      setPercent(1);
      if (status === 'done') {
        const response = { ...info.file.response };

        if (multiple || single || singlePerRow) {
          if (docs[fileCode] && docs[fileCode][index] === '') {
            const updatedDocs = newDocs;
            updatedDocs[index] = response;
            setNewDocs(updatedDocs);
          }
        }

        const newValue = multiple
          ? docs[fileCode]
            ? [...docs[fileCode], response]
            : [response]
          : single || singlePerRow
          ? docs[fileCode]
            ? docs[fileCode][index] !== ''
              ? [...docs[fileCode], response]
              : newDocs
            : [response]
          : response;
        dispatch(
          updateFormField({
            group,
            key: fileCode,
            value: newValue,
          })
        );

        if (isExcelToParse) {
          // dispatch(
          //   parseExcel({
          //     id: responseId,
          //     name: info.file.name,
          //     callback: success => {
          //       setPercent(100);
          //       dispatch(triggerLoading(false));
          //     },
          //   })
          // );
        } else {
          setPercent(100);
          dispatch(triggerLoading(false));
        }

        //autosave
        // const dataToSave = { ...saveData };
        // dataToSave.state.documents[fileCode] = newValue;
        // console.log("fileCode:",fileCode);
        // // if(fileCode !== "investmentExpenses"){
        //   handleSave(dataToSave);
        // // }

        if (fileCode === 'contractProvider') {
          dispatch(addContractDate(forms?.task?.processInstanceId));
        }
      }

      if (mode === 'ktp-uploads') {
        setPercent(100);
        dispatch(triggerLoading(false));
      }
    },
    showUploadList: false,
    progress: {
      format: percent => percent && `${parseFloat(percent.toFixed(2))}%`,
    },
  };

  const handleDeleteDocument = (event, file) => {
    event.stopPropagation();
    event.preventDefault();
    console.log(docs[fileCode]);

    const newValue = multiple
      ? size(docs[fileCode]) === 1
        ? null
        : Array.isArray(docs[fileCode])
        ? docs[fileCode]?.filter(doc => doc.id !== file.id)
        : null
      : single || singlePerRow
      ? size(docs[fileCode]) === 1
        ? ''
        : docs[fileCode]?.map(doc => {
            if (!isNil(doc) && doc.id === file.id) {
              return '';
            }
            return doc;
          })
      : null;

    if (mode === 'ktp-uploads') {
      dispatch(
        updateFormField({
          group,
          key: fileCode,
          value: newValue,
        })
      );
      handleFile();
    } else {
      console.log("ENTEREDDD");
      dispatch(
        deleteDocument({
          docId: Array.isArray(docs[fileCode])
            ? docs[fileCode]?.find(doc => doc.id === file.id)?.id
            : docs[fileCode]?.id,
          group,
          key: fileCode,
          value: newValue,
        })
      );
      dispatch(
        updateFormField({
          group,
          key: fileCode,
          value: newValue,
        })
      );
      // console.log("ENTERED HERE");
      if(fileCode === "investmentExpenses"){
        dispatch(
          updateFormField({
            group: "carrier",
            key: "dapanesEpendysis",
            value: "",
          })
        );
      }
      //autosave
      // const dataToSave = { ...saveData };
      // dataToSave.state.documents[fileCode] = newValue;
      // if(fileCode === "investmentExpenses"){
      //   saveData.state.carrier.dapanesEpendysis = "";
      // }
      // handleSave(saveData);

      // if (fileCode === 'contractProvider') {
      //   dispatch(deleteContractDate(forms?.task?.processInstanceId));
      // }
    }

    // if (isExcelToParse) {
    //   dispatch(clearExcelData());
    // }
  };

  useEffect(() => {
    if (fileCode === 'certificationDocs') {
      // || fileCode === 'ibaDocs'
      if (docs.certificationDocs) {
        //docs.ibaDocs &&
        dispatch(setValidity({ group: 'documents', value: true }));
      } else {
        dispatch(setValidity({ group: 'documents', value: false }));
      }
    }
  }, [docs, fileCode, dispatch]);

  return (
    <div className={`ant-drag-container ${disabled ? 'upload-disabled' : ''}`}>
      <WarningModal
        isVisible={excelErrorModalVisible}
        setVisible={setExcelErrorModalVisible}
        // Icon={icon}
        // closable={true}
        //   title={title}
        // handleConfirm={() => setVisible(false)}
        onCancel={() => setExcelErrorModalVisible(false)}
        title={"Σφάλμα κατά τη μεταφόρτωση"}
        message={errorMessages?.map((msg, i) => (
            <div key={i} style={{marginBottom: '8px', fontSize: '14px' }}>
              • {msg}
            </div>
          ))}
        width={800}
        footer={
          [
            <Button
              type={'default'}
              style={{width: '100%'}}
              onClick={() => setExcelErrorModalVisible(false)}
            >Κλείσιμο</Button>,
          ]
        }
        className={'invalid-modal custom-modal warning-modal'}
      >
        <div className="custom-modal__header"><WarningRed/><span>{""}</span></div>
        <div className="custom-modal__body">{}</div>
      </WarningModal>
      {label && isNil(registerLabel) && (
        <UploadLabel
          label={label}
          subLabel={subLabel}
          linkText={linkText}
          fileCode={fileCode}
          required={required}
        />
      )}
      {!isNil(registerLabel) && (
        <UploadLabel
          label={registerLabel}
          subLabel={subLabel}
          linkText={linkText}
          fileCode={fileCode}
          required={required}
        />
      )}
      <Dragger
        {...props}
        {...rest}
        disabled={disabled}
        id={fileCode}
        className={`custom-drag ${
          docs[fileCode] && multiple && size(docs[fileCode]) > 2
            ? 'with-scroll-x'
            : ''
        }`}
      >
        <UploadTitle fileCode={fileCode} />
        {errorFile || shouldDisplayExcelError ? (
          <File
            error
            file={{
              fileLimit,
              // description: notAccepted
              //   ? 'Ο τύπος του αρχείου δεν είναι αποδεκτός'
              //   : excel? 'Σφάλμα κατα τη μεταφόρτωση του αρχείου'
              //   : generic? 'Παρουσιάστηκε σφάλμα κατά τη μεταφόρτωση του αρχείου. Παρακαλούμε δοκιμάστε ξανά.'
              //   :'The uploaded file is too large.',
              description: errorDescription,
              name: shouldDisplayExcelError ? excelError.name : errorFile,
              showMax: false
            }}
            handleDeleteDocument={(event, file) => {
              event.stopPropagation();
              event.preventDefault();
              setError('');
              if (shouldDisplayExcelError) {
                dispatch(clearExcelError());
                dispatch(
                  updateFormField({ group, key: 'ibaBusXls', value: null })
                );
              }
            }}
          />
        ) : docs[fileCode] ? (
          isArray(docs[fileCode]) && !single ? (
            <div className="d-flex custom-drag__filelist-container">
              {docs[fileCode].map((file, index) => (
                <File
                  key={index}
                  file={file}
                  handleDeleteDocument={handleDeleteDocument}
                  percent={percent}
                />
              ))}
            </div>
          ) : single ? (
            docs[fileCode][index] && !isNil(docs[fileCode][index].name) ? (
              <div className="d-flex custom-drag">
                <File
                  key={index}
                  file={docs[fileCode][index]}
                  handleDeleteDocument={handleDeleteDocument}
                  percent={percent}
                />
              </div>
            ) : (
              <ClickUpload fileAccepts={fileAccepts} fileLimit={fileLimit} fileCode={fileCode}/>
            )
          ) : docs[fileCode][index] && !isNil(docs[fileCode][index].name) ? (
            <div className="">
              {docs[fileCode].map((file, index) => (
                <div className="d-flex mt-2">
                  <File
                    key={index}
                    file={docs[fileCode][index]}
                    handleDeleteDocument={handleDeleteDocument}
                    percent={percent}
                  />
                  <div className="download-icon ml-4">
                    <DownloadSingleFile
                      item={{
                        fileCode: fileCode,
                        fileLimit: 20,
                        single: true,
                      }}
                      task={task}
                      index={index}
                      group={group}
                      withOpen={withOpen}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <File
              file={docs[fileCode]}
              handleDeleteDocument={handleDeleteDocument}
              percent={percent}
            />
          )
        ) : (
          <ClickUpload fileAccepts={fileAccepts} fileLimit={fileLimit} fileCode={fileCode}/>
        )}
      </Dragger>
    </div>
  );
};

Draggable.propTypes = {
  label: PropTypes.string,
  subLabel: PropTypes.string,
  linkText: PropTypes.string,
  group: PropTypes.string.isRequired,
  required: PropTypes.bool,
};

export default Draggable;
